export function evidenceCountLabel(value: number | null): string {
  return value === null ? 'Unavailable' : value.toLocaleString();
}

export function evidenceDraftId(
  surfaceDraftId: string | undefined,
  attribution: { status: 'attributed'; draftId: string } | { status: 'legacy'; draftId: null } | undefined,
): string | null {
  if (surfaceDraftId) return surfaceDraftId;
  return attribution?.status === 'attributed' ? attribution.draftId : null;
}

export function normalizeEvidenceTimestamp(value: string): string {
  return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
}

export function evidenceSourceRevisionLabel(
  sourceRevision: string | null,
): string {
  if (!sourceRevision) return 'Unavailable';
  const prefix = 'sha256:';
  const digest = sourceRevision.startsWith(prefix)
    ? sourceRevision.slice(prefix.length)
    : sourceRevision;
  return digest.length > 12
    ? `${sourceRevision.startsWith(prefix) ? prefix : ''}${digest.slice(0, 12)}…`
    : sourceRevision;
}

export function preflightEstimateLabel(preflight: {
  estimatedRequestCount: number;
  requestedRecordCounts: { customers: number; providers: number };
}): string {
  return `${evidenceCountLabel(preflight.estimatedRequestCount)} provider requests estimated for ${evidenceCountLabel(preflight.requestedRecordCounts.customers)} Customer and ${evidenceCountLabel(preflight.requestedRecordCounts.providers)} Provider records.`;
}

const RUN_STATUS_LABELS = {
  pending: 'Pending',
  running: 'Running',
  complete: 'Complete',
  failed: 'Failed',
  interrupted: 'Interrupted',
  cancelled: 'Cancelled',
} as const;

export function canRetryEvidenceRun(status: keyof typeof RUN_STATUS_LABELS): boolean {
  return status === 'failed' || status === 'interrupted';
}

export function evidenceRunHeading(run: {
  retryCount: number;
  status: keyof typeof RUN_STATUS_LABELS;
  completeness: 'pending' | 'complete' | 'incomplete';
  warnings?: string[];
}): string {
  const completeness = run.completeness === 'complete'
    ? run.warnings?.includes('provider_records_not_returned')
      ? 'Partial coverage'
      : 'Complete evidence'
    : run.completeness === 'pending'
      ? 'Evidence pending'
      : 'Incomplete';
  return `Run ${run.retryCount + 1} · ${RUN_STATUS_LABELS[run.status]} · ${completeness}`;
}

export function evidenceAttemptHeading(attempt: {
  periodKind: 'current' | 'comparison';
  partyType: 'customer' | 'provider';
  batchIndex: number;
  attemptNumber: number;
}): string {
  const period = attempt.periodKind === 'current' ? 'Current' : 'Comparison';
  const party = attempt.partyType === 'customer' ? 'Customer' : 'Provider';
  return `${period} · ${party} · Batch ${attempt.batchIndex + 1} · Attempt ${attempt.attemptNumber}`;
}

/**
 * Coverage of mapped trunks. A trunk with no returned row is not zero traffic,
 * so totals include only the trunks that returned data.
 */
export function trunkCoverageNotice(party: {
  mappedTrunkCount: { current: number; comparison: number };
  observedTrunkCount: { current: number; comparison: number };
}): string | null {
  const { mappedTrunkCount: mapped, observedTrunkCount: observed } = party;
  if (observed.current >= mapped.current && observed.comparison >= mapped.comparison) return null;
  return `Data returned for ${observed.current} of ${mapped.current} mapped trunks in the current period `
    + `and ${observed.comparison} of ${mapped.comparison} in the comparison period. `
    + 'SipNav returned no data for the others. Totals include only the trunks that returned data.';
}
